// iOS memory fix: keep at most WINDOW_PAGES pages of cards in the DOM.
// Pages scrolled far out of view are swapped for a spacer of the same height
// and fetched again (Section Rendering API) when the shopper scrolls back.
const WINDOW_PAGES = 3;
const REFILL_MARGIN = 1200;
// Raw grid HTML of recently fetched pages, so scrolling back up refills
// instantly instead of waiting on the network. Strings are cheap next to DOM.
const PAGE_CACHE_SIZE = 12;
const pageCache = new Map();
// Keyed by the current filters/sort as well, since page N changes with them.
function pageCacheKey(page) {
  const params = new URLSearchParams(window.location.search);
  params.delete('page');
  params.delete('section_id');
  return `${window.location.pathname}?${params}#${page}`;
}
function cachePage(page, html) {
  if (!page) return;
  const key = pageCacheKey(page);
  pageCache.delete(key);
  pageCache.set(key, html);
  if (pageCache.size > PAGE_CACHE_SIZE) pageCache.delete(pageCache.keys().next().value);
}
function cachedPage(page) {
  return pageCache.get(pageCacheKey(page));
}

class CollectionWindow {
  static get(grid) {
    if (!grid.__collectionWindow || !grid.__collectionWindow.isValid()) {
      grid.__collectionWindow = new CollectionWindow(grid);
    }
    return grid.__collectionWindow;
  }

  constructor(grid) {
    this.grid = grid;
    this.top = []; // trimmed pages above, ascending (nearest = last)
    this.bottom = []; // trimmed pages below, ascending (nearest = first)
    this.heights = {}; // page -> slot height (block height + one row gap)
    this.topSpacer = null;
    this.bottomSpacer = null;
    this.busy = false;
    this.observer = new IntersectionObserver(this.onSpacerIntersect.bind(this), {
      rootMargin: `${REFILL_MARGIN}px 0px ${REFILL_MARGIN}px 0px`,
    });
  }

  // Facet changes replace the grid markup; start over when our spacers are gone.
  isValid() {
    return (
      this.grid.isConnected &&
      (!this.topSpacer || this.topSpacer.isConnected) &&
      (!this.bottomSpacer || this.bottomSpacer.isConnected)
    );
  }

  get rowGap() {
    return parseFloat(getComputedStyle(this.grid).rowGap) || 0;
  }

  // Map of page -> elements, in DOM order. Promo tiles have no data-page and
  // belong to the page of the card before them (or after, at the very start).
  pageGroups() {
    const groups = new Map();
    const pending = [];
    let current = null;
    for (const el of this.grid.children) {
      if (el === this.topSpacer || el === this.bottomSpacer) continue;
      const page = el.dataset.page ? Number(el.dataset.page) : null;
      if (page === null && current === null) {
        pending.push(el);
        continue;
      }
      const key = page ?? current;
      current = key;
      if (!groups.has(key)) groups.set(key, []);
      if (pending.length) groups.get(key).push(...pending.splice(0));
      groups.get(key).push(el);
    }
    return groups;
  }

  // Apps (Swatch King's swatch lists in particular) keep references to card
  // elements after they leave the DOM. Emptying removed cards means those
  // references hold an empty shell instead of the whole card and its images.
  static release(el) {
    el.querySelectorAll('ul, ol').forEach((list) => list.replaceChildren());
    el.replaceChildren();
  }

  hasPages() {
    return this.grid.querySelector(':scope > [data-page]') !== null;
  }

  makeSpacer(position) {
    const li = document.createElement('li');
    li.className = 'grid-spacer';
    li.setAttribute('aria-hidden', 'true');
    li.style.cssText = 'flex:0 0 100%;width:100%;max-width:100%;padding:0;margin:0;visibility:hidden;pointer-events:none;';
    if (position === 'top') this.grid.prepend(li);
    else this.grid.append(li);
    this.observer.observe(li);
    return li;
  }

  sumHeights(pages) {
    return pages.reduce((total, page) => total + (this.heights[page] || 0), 0);
  }

  syncSpacers() {
    const gap = this.rowGap;
    if (this.top.length) {
      if (!this.topSpacer) this.topSpacer = this.makeSpacer('top');
      this.topSpacer.style.height = `${Math.max(this.sumHeights(this.top) - gap, 0)}px`;
    } else if (this.topSpacer) {
      this.observer.unobserve(this.topSpacer);
      this.topSpacer.remove();
      this.topSpacer = null;
    }
    if (this.bottom.length) {
      if (!this.bottomSpacer) this.bottomSpacer = this.makeSpacer('bottom');
      this.bottomSpacer.style.height = `${Math.max(this.sumHeights(this.bottom) - gap, 0)}px`;
    } else if (this.bottomSpacer) {
      this.observer.unobserve(this.bottomSpacer);
      this.bottomSpacer.remove();
      this.bottomSpacer = null;
    }
  }

  // Keeps the first visible card in place while content above it changes.
  // Safari has no CSS scroll anchoring, so this has to be done by hand.
  withAnchor(getAnchor, change) {
    const anchor = getAnchor();
    const before = anchor ? anchor.getBoundingClientRect().top : null;
    change();
    if (anchor && anchor.isConnected) {
      const delta = anchor.getBoundingClientRect().top - before;
      if (Math.abs(delta) > 1) window.scrollBy(0, delta);
    }
  }

  trim(side) {
    if (!this.hasPages()) return;
    let groups = this.pageGroups();
    while (groups.size > WINDOW_PAGES) {
      const pages = [...groups.keys()];
      if (side === 'top') {
        const page = pages[0];
        const els = groups.get(page);
        const next = groups.get(pages[1])[0];
        this.heights[page] = next.getBoundingClientRect().top - els[0].getBoundingClientRect().top;
        this.withAnchor(
          () => next,
          () => {
            els.forEach((el) => {
              el.remove();
              CollectionWindow.release(el);
            });
            this.top.push(page);
            this.syncSpacers();
          }
        );
      } else {
        const page = pages[pages.length - 1];
        const els = groups.get(page);
        const after = els[els.length - 1].nextElementSibling;
        const start = els[0].getBoundingClientRect().top;
        this.heights[page] = after
          ? after.getBoundingClientRect().top - start
          : this.grid.getBoundingClientRect().bottom - start + this.rowGap;
        els.forEach((el) => {
          el.remove();
          CollectionWindow.release(el);
        });
        this.bottom.unshift(page);
        this.syncSpacers();
      }
      groups = this.pageGroups();
    }
  }

  // New page from Load more / infinite scroll.
  append(page, nodes) {
    if (this.bottom.length) {
      // Shopper scrolled back up meanwhile: queue it below the window instead.
      const known = Object.values(this.heights);
      this.heights[page] = known.length ? known.reduce((a, b) => a + b, 0) / known.length : 0;
      this.bottom.push(page);
      this.syncSpacers();
      return;
    }
    this.grid.append(...nodes);
    this.trim('top');
  }

  // Used by collection-state.js when Back restores a window that starts after page 1.
  restoreAbove(firstPage, heights) {
    this.top = [];
    for (let page = 1; page < firstPage; page++) {
      this.top.push(page);
      this.heights[page] = (heights && heights[page]) || 0;
    }
    this.syncSpacers();
  }

  snapshot() {
    const pages = [...this.pageGroups().keys()];
    return { firstPage: pages[0] || 1, lastPage: pages[pages.length - 1] || 1, heights: { ...this.heights } };
  }

  onSpacerIntersect(entries) {
    if (entries.some((entry) => entry.isIntersecting)) this.refillNearest();
  }

  spacerNearViewport(spacer) {
    if (!spacer) return false;
    const rect = spacer.getBoundingClientRect();
    return rect.bottom > -REFILL_MARGIN && rect.top < window.innerHeight + REFILL_MARGIN;
  }

  async refillNearest() {
    if (this.busy || !this.isValid()) return;
    const side = this.spacerNearViewport(this.topSpacer) ? 'top' : this.spacerNearViewport(this.bottomSpacer) ? 'bottom' : null;
    if (!side) return;

    this.busy = true;
    const page = side === 'top' ? this.top[this.top.length - 1] : this.bottom[0];
    try {
      let incoming;
      const cached = cachedPage(page);
      if (cached) {
        incoming = document.createElement('template');
        incoming.innerHTML = cached;
        incoming = incoming.content;
      } else {
        const doc = await InfiniteScroll.fetchSection(InfiniteScroll.pageUrl(page));
        incoming = doc && doc.getElementById('product-grid');
        if (incoming) cachePage(page, incoming.innerHTML);
      }
      if (!incoming || !this.isValid()) return;
      const nodes = [...incoming.children];
      nodes.forEach((el) => {
        if (!el.dataset.page) el.dataset.page = page;
      });

      if (side === 'top') {
        this.withAnchor(
          () => this.topSpacer.nextElementSibling,
          () => {
            this.topSpacer.after(...nodes);
            this.top.pop();
            this.syncSpacers();
          }
        );
        this.trim('bottom');
      } else {
        this.bottomSpacer.before(...nodes);
        this.bottom.shift();
        this.syncSpacers();
        this.trim('top');
      }
    } catch (e) {
      console.error(e);
    } finally {
      this.busy = false;
    }
    // Fast scrolling can leave a spacer in view after one refill.
    if (this.spacerNearViewport(this.topSpacer) || this.spacerNearViewport(this.bottomSpacer)) {
      requestAnimationFrame(() => this.refillNearest());
    }
  }
}
window.CollectionWindow = CollectionWindow;

class InfiniteScroll extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', this.onClickHandler.bind(this));

    if (this.dataset.trigger == 'infinite') {
      this.observer = new IntersectionObserver(this.handleIntersection.bind(this), {rootMargin: '0px 0px 200px 0px'});
      this.observer.observe(this);
    }
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  onClickHandler() {
    if (this.classList.contains('loading') || this.classList.contains('disabled')) return;
    this.classList.add('loading');
    this.classList.add('disabled');

    InfiniteScroll.renderSectionFromFetch(this.dataset.url, this);
  }

  handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting) return;
    observer.unobserve(this);

    this.onClickHandler();
  }

  static sectionId() {
    const grid = document.getElementById('product-grid');
    return grid ? grid.dataset.id : null;
  }

  static withSectionId(url) {
    const id = InfiniteScroll.sectionId();
    const u = new URL(url, window.location.origin);
    if (id) u.searchParams.set('section_id', id);
    return u.toString();
  }

  static pageUrl(page) {
    const u = new URL(window.location.href);
    u.hash = '';
    u.searchParams.set('page', page);
    return InfiniteScroll.withSectionId(u.toString());
  }

  // Fetch only the grid section (not the whole page) and parse it once.
  static async fetchSection(url) {
    // Time out so a stalled request (bad network, app fetch wrappers) can't
    // leave the window stuck.
    const controller = window.AbortController ? new AbortController() : null;
    let timer;
    const timeout = new Promise((resolve, reject) => {
      timer = setTimeout(() => {
        if (controller) controller.abort();
        reject(new Error('Load more timed out'));
      }, 20000);
    });
    let response;
    try {
      response = await Promise.race([fetch(url, controller ? { signal: controller.signal } : undefined), timeout]);
    } finally {
      clearTimeout(timer);
    }
    if (!response.ok) throw new Error(`Load more failed: ${response.status}`);
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  }

  static renderSectionFromFetch(url, trigger) {
    const page = Number(new URL(url, window.location.origin).searchParams.get('page')) || null;
    InfiniteScroll.fetchSection(InfiniteScroll.withSectionId(url))
      .then((doc) => {
        InfiniteScroll.renderPagination(doc);
        InfiniteScroll.renderProductGridContainer(doc, page);
      })
      .catch((e) => {
        console.error(e);
        if (trigger) trigger.classList.remove('loading', 'disabled');
      });
  }

  static renderPagination(doc) {
    const container = document.getElementById('ProductGridContainer').querySelector('.pagination-wrapper');
    const incoming = doc.getElementById('ProductGridContainer');
    const pagination = incoming && incoming.querySelector('.pagination-wrapper');
    if (!container) return;
    if (pagination) {
      container.innerHTML = pagination.innerHTML;
    }
    else {
      container.remove();
    }
  }

  static renderProductGridContainer(doc, page) {
    const container = document.getElementById('product-grid');
    const products = doc.getElementById('product-grid');
    if (!container || !products) return;
    cachePage(page, products.innerHTML);
    const nodes = [...products.children];
    if (page) {
      nodes.forEach((el) => {
        if (!el.dataset.page) el.dataset.page = page;
      });
    }
    CollectionWindow.get(container).append(page, nodes);
  }
}
customElements.define('infinite-scroll', InfiniteScroll);
